/**
 * O snippet que se cola no site do cliente, e a decisão de fixar ou não a versão do SDK.
 *
 * O que estava errado aqui era a hierarquia: o primeiro elemento da tela era um aviso de três
 * parágrafos sobre cache de CDN, e o snippet — que é o motivo de alguém abrir esta tela — vinha
 * depois dele. Agora o snippet é o primeiro card; o aviso de propagação virou uma linha abaixo do
 * botão de copiar, que é quando ele passa a importar.
 *
 * O `<pre>` ganhou `tabIndex`: bloco com rolagem horizontal precisa ser alcançável por teclado,
 * senão quem não usa mouse não consegue ler o fim da linha.
 */

import { useEffect, useState } from 'react';
import {
  CONFIG_CACHE_SECONDS,
  CONFIG_PROPAGATION_SECONDS,
  CONFIG_STORE_TTL_MS,
} from '@merlin/flow-engine';
import { useEditor } from '../../store/editor.js';
import { Badge, Button, Field, Input, Notice, Section, Switch } from '../ui.js';
import { IconeCheck, IconeCopiar, IconeInstalar } from '../icons.js';

/** Manifesto publicado ao lado do widget por `scripts/copy-widget.mjs`. */
interface WidgetManifest {
  latest: string;
  versions: string[];
}

export function InstallSection({ apiBase, widgetSrc }: { apiBase: string; widgetSrc: string }) {
  const publicKey = useEditor((state) => state.publicKey);
  const publishedVersion = useEditor((state) => state.publishedVersion);
  const [copied, setCopied] = useState(false);
  const [manifest, setManifest] = useState<WidgetManifest | null>(null);
  const [pinned, setPinned] = useState(false);

  /*
   * Lista de versões publicadas. Falha em silêncio de propósito: o snippet do canal atualizado
   * funciona sem manifesto, e a pinagem é o recurso extra.
   */
  useEffect(() => {
    let origem: string;
    try {
      origem = new URL(widgetSrc).origin;
    } catch {
      return;
    }
    let ativo = true;
    void fetch(`${origem}/widget-manifest.json`)
      .then((resposta) => (resposta.ok ? resposta.json() : null))
      .then((dados: unknown) => {
        const parsed = dados as WidgetManifest | null;
        if (ativo && parsed && typeof parsed.latest === 'string') setManifest(parsed);
      })
      .catch(() => {
        /* sem manifesto, sem pinagem */
      });
    return () => {
      ativo = false;
    };
  }, [widgetSrc]);

  const versao = manifest?.latest ?? null;

  /** Caminho imutável da versão corrente. É o que o snippet fixado usa. */
  const srcFixado = ((): string => {
    if (!versao) return widgetSrc;
    try {
      const url = new URL(widgetSrc);
      return `${url.origin}/widget/${versao}/widget.js`;
    } catch {
      return widgetSrc;
    }
  })();

  const snippet = [
    '<script',
    `  src="${pinned ? srcFixado : widgetSrc}"`,
    `  data-webchat-id="${publicKey}"`,
    ...(apiBase ? [`  data-api-base="${apiBase}"`] : []),
    '  async></script>',
  ].join('\n');

  return (
    <div className="space-y-6">
      {publishedVersion === null ? (
        <Notice tone="warn" title="Este webchat ainda não foi publicado">
          O snippet abaixo já funciona, mas o servidor vai responder <code>404 not_published</code>{' '}
          até você clicar em Publicar.
        </Notice>
      ) : null}

      <Section
        icon={IconeInstalar}
        title="Cole no <head> do site"
        description="Uma tag, nada mais. Pode ir também como Custom HTML no GTM."
        action={
          <Button
            variant="primary"
            onClick={() => {
              void navigator.clipboard.writeText(snippet).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            {copied ? <IconeCheck /> : <IconeCopiar />}
            {copied ? 'Copiado!' : 'Copiar'}
          </Button>
        }
      >
        {/*
          `tabIndex={0}` porque o bloco rola na horizontal: sem ele, quem navega por teclado não
          alcança o fim de uma linha longa. O `aria-label` nomeia a região para o leitor de tela.
        */}
        <pre
          tabIndex={0}
          aria-label="Snippet de instalação"
          className="overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs leading-relaxed text-slate-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          {snippet}
        </pre>

        {publishedVersion !== null ? (
          <Notice
            tone="info"
            title={`Depois de publicar, a mudança aparece em ~${CONFIG_CACHE_SECONDS}s`}
          >
            <p>
              A config é cacheada {CONFIG_CACHE_SECONDS}s no navegador e {CONFIG_CACHE_SECONDS}s no
              CDN, e o servidor guarda o webchat por mais {CONFIG_STORE_TTL_MS / 1000}s — as camadas
              somam, então o pior caso é {CONFIG_PROPAGATION_SECONDS}s. O{' '}
              <strong>Preview</strong> aqui do painel não passa por nada disso: ele lê o rascunho
              direto.
            </p>
            {/*
              A receita concreta, que só passou a existir com o `data-merlin-config` no host do widget.
              Antes a única forma de conferir era recarregar e olhar a conversa — e no meio do caminho
              ficava a dúvida de ser cache ou ser a alteração que não salvou.
            */}
            <p>
              Para conferir se já chegou, no console da página do cliente:{' '}
              <code>
                document.querySelector('[data-merlin-webchat]').dataset.merlinConfig
              </code>{' '}
              — enquanto não for <strong>{publishedVersion}</strong>, o cache ainda está servindo a
              versão anterior.
            </p>
            <p>
              Existe também <code>data-no-cache="true"</code> na tag, que desvia do cache por completo.
              Com {CONFIG_CACHE_SECONDS}s de espera ele raramente se justifica, e só vale em página de
              teste: sem cache, cada visita vira uma chamada ao servidor.
            </p>
          </Notice>
        ) : null}
      </Section>

      <Section
        title="Versão do SDK"
        description={
          versao ? `O canal atualizado está na ${versao}.` : 'Manifesto indisponível — sem pinagem.'
        }
        action={versao ? <Badge>{versao}</Badge> : null}
      >
        {versao ? (
          <>
            <Switch
              checked={pinned}
              label="Fixar nesta versão"
              description={
                <>
                  Desligado, o site usa <code>/widget.js</code> e recebe nossas atualizações sozinho
                  (cache de 5 minutos). Ligado, o snippet acima aponta para um caminho{' '}
                  <strong>imutável</strong> e o site fica nesta versão até você trocar — útil para
                  site crítico, ao custo de não receber correção automática.
                </>
              }
              onChange={setPinned}
            />
            {manifest && manifest.versions.length > 1 ? (
              <p className="text-sm text-slate-500">Publicadas: {manifest.versions.join(', ')}</p>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-slate-500">
            O snippet usa o canal atualizado, que funciona sem o manifesto.
          </p>
        )}

        <Field label="Chave pública" hint="Identifica este webchat na chamada de configuração.">
          <Input value={publicKey} readOnly className="font-mono text-xs" />
        </Field>
      </Section>
    </div>
  );
}
